<template>
  <div class="demo-manager">
    <!-- Overlay para móvil - Bloquea el acceso -->
    <div v-if="showMobileWarning && isMobileDevice" class="demo-overlay mobile-warning-overlay">
      <div class="overlay-background"></div>
      <div class="overlay-content mobile-warning-content">
        <div class="warning-icon">📱</div>
        <h2>ACCESO NO DISPONIBLE EN MÓVIL</h2>
        <p>Para una experiencia óptima, accede a este demo desde un dispositivo de escritorio (PC/Laptop).</p>
        <p class="warning-note">Los demos están optimizados para pantallas grandes y funcionalidades de desktop.</p>

        <div class="mobile-actions">
          <button @click="redirectToExamples" class="mobile-btn secondary-btn">
            Volver a Ejemplos
          </button>
          <button @click="continueAnyway" class="mobile-btn primary-btn">
            Continuar de Todas Formas
          </button>
        </div>

        <!-- Configuración fácil de desactivar -->
        <div class="config-note" v-if="mobileWarningEnabled">
          <p>🔧 <small>Esta restricción puede desactivarse en cualquier momento</small></p>
        </div>
      </div>
    </div>

    <!-- Overlay explicativo normal - SOLO para desktop -->
    <div v-else-if="showOverlay" class="demo-overlay">
      <div class="overlay-background"></div>
      <div class="overlay-content">
        <div class="spiral-arrow">🌀</div>
        <div class="logo-container">
          <img
            src="../assets/images/Logo.png"
            width="60"
            height="60"
            alt="Logo"
          />
        </div>
        <h2>ATENCIÓN:Vista Demo Activa</h2>
        <p>Toca el logo en la esquina superior izquierda para activar/desactivar el menú de navegación</p>

        <!-- Checkbox "No mostrar de nuevo" -->
        <div class="dont-show-container">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="dontShowAgain"
              class="checkbox-input"
            >
            <span class="checkmark"></span>
            No mostrar de nuevo
          </label>
        </div>

        <button @click="closeOverlay" class="understand-btn">
          ENTENDIDO
        </button>
      </div>
    </div>

    <!-- Diálogo de Información -->
    <div v-if="showInfoDialog" class="info-dialog-overlay" @click="closeInfoDialog">
      <div class="info-dialog" @click.stop>
        <div class="dialog-header">
          <h3>Información del Demo</h3>
          <button class="close-dialog-btn" @click="closeInfoDialog">×</button>
        </div>
        <div class="dialog-content">
          <div class="demo-info">
            <h4>{{ currentDemoName }}</h4>
            <p class="demo-description">{{ getDemoDescription() }}</p>

            <div class="tech-stack">
              <h5>Tecnologías Utilizadas:</h5>
              <div class="tech-tags">
                <span v-for="tech in getDemoTechnologies()" :key="tech" class="tech-tag">
                  {{ tech }}
                </span>
              </div>
            </div>

            <div class="demo-features">
              <h5>Características:</h5>
              <ul>
                <li v-for="feature in getDemoFeatures()" :key="feature">{{ feature }}</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button @click="closeInfoDialog" class="dialog-close-btn">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Controles del demo - SOLO cuando no hay overlay Y cuando deben mostrarse -->
    <div class="demo-controls" v-if="!showOverlay && !showMobileWarning && isDemoActive && showControls">
      <div class="controls-dropdown" v-if="showDropdown">
        <div class="dropdown-header">
          {{ currentDemoName }}
        </div>
        <div class="dropdown-divider"></div>
        <button @click="showInfoDialog = true" class="dropdown-item info-btn">
          ⓘ Información
        </button>
        <button @click="exitDemo" class="dropdown-item exit-btn">
          ✕ Salir del Demo
        </button>

        <!-- Configuración rápida para desarrollo -->
        <div class="dropdown-divider" v-if="mobileWarningEnabled"></div>
        <button @click="toggleMobileWarning" class="dropdown-item config-btn" v-if="mobileWarningEnabled">
          🔧 {{ mobileWarningEnabled ? 'Desactivar' : 'Activar' }} Bloqueo Móvil
        </button>
      </div>

      <!-- Botón de controles -->
      <button
        class="controls-toggle"
        @click="toggleDropdown"
        @blur="onBlurControls"
      >
        ···
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DemoManager',
  data () {
    return {
      showOverlay: false,
      showMobileWarning: false,
      showDropdown: false,
      showInfoDialog: false,
      currentProjectId: null,
      demoOverlaySeen: false,
      isDemoActive: false,
      showControls: false,
      dontShowAgain: false,
      isMobileDevice: false,
      // CONFIGURACIÓN FÁCIL - Cambiar a false para desactivar bloqueo móvil
      mobileWarningEnabled: true
    }
  },
  computed: {
    currentDemoName () {
      const demoMap = {
        'modo-claro-oscuro': 'Modo Claro/Oscuro',
        'responsive-control': 'Control Responsive',
        'animaciones-scroll': 'Animaciones Scroll',
        'web-ficticia': 'Sitio Web Ficticio',
        'web-mejorada': 'Web Mejorada',
        'clon-netflix': 'Clon Netflix'
      };
      return demoMap[this.currentProjectId] || 'Demo';
    }
  },
  mounted () {
    this.checkDeviceType();
    this.checkFirstTime();
    this.setupEventListeners();
  },
  methods: {
    checkDeviceType () {
      // Detectar si es dispositivo móvil
      this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                           window.innerWidth < 768;

      console.log('📱 Dispositivo detectado:', this.isMobileDevice ? 'Móvil' : 'Desktop');
    },

    checkFirstTime () {
      const seen = localStorage.getItem('demoOverlaySeen');
      this.demoOverlaySeen = seen === 'true';

      // Mostrar overlay normal solo si no es móvil O si está desactivado el bloqueo
      if (!this.mobileWarningEnabled || !this.isMobileDevice) {
        this.showOverlay = !this.demoOverlaySeen;
      }

      if (this.demoOverlaySeen) {
        this.isDemoActive = true;
      }
    },

    setupEventListeners () {
      window.addEventListener('load-demo-manager', (event) => {
        this.currentProjectId = event.detail.projectId;
        console.log('📦 DemoManager cargado para:', this.currentProjectId);

        // Verificar si debe mostrar advertencia móvil
        if (this.mobileWarningEnabled && this.isMobileDevice) {
          this.showMobileWarning = true;
          this.isDemoActive = false;
        } else {
          this.isDemoActive = true;
          const seen = localStorage.getItem('demoOverlaySeen');
          this.showOverlay = seen !== 'true';
        }
      });

      window.addEventListener('deactivate-demo-mode', () => {
        this.deactivateDemo();
      });

      window.addEventListener('demo-controls-visibility', (event) => {
        this.showControls = event.detail.showControls;
        console.log('🎛️ Controles ···:', this.showControls ? 'VISIBLES' : 'OCULTOS');

        if (!this.showControls) {
          this.showDropdown = false;
        }
      });
    },

    closeOverlay () {
      this.showOverlay = false;
      this.isDemoActive = true;

      if (this.dontShowAgain) {
        localStorage.setItem('demoOverlaySeen', 'true');
        this.demoOverlaySeen = true;
      }
    },

    // Métodos para manejo móvil
    redirectToExamples () {
      this.$router.push('/Ejemplos');
    },

    continueAnyway () {
      this.showMobileWarning = false;
      this.isDemoActive = true;
      this.showOverlay = !this.demoOverlaySeen;
    },

    toggleMobileWarning () {
      this.mobileWarningEnabled = !this.mobileWarningEnabled;
      localStorage.setItem('mobileWarningEnabled', this.mobileWarningEnabled.toString());
      console.log('🔧 Bloqueo móvil:', this.mobileWarningEnabled ? 'ACTIVADO' : 'DESACTIVADO');
    },

    toggleDropdown () {
      this.showDropdown = !this.showDropdown;
    },

    onBlurControls () {
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },

    exitDemo () {
      console.log('🚪 Saliendo del modo demo');
      this.showDropdown = false;
      this.isDemoActive = false;
      this.showControls = false;

      window.dispatchEvent(new CustomEvent('deactivate-demo-mode'));
      this.$router.push('/Ejemplos');
    },

    deactivateDemo () {
      console.log('🔴 DemoManager desactivado');
      this.isDemoActive = false;
      this.showDropdown = false;
      this.showControls = false;
      this.showInfoDialog = false;
    },

    closeInfoDialog () {
      this.showInfoDialog = false;
      this.showDropdown = false;
    },

    getDemoDescription () {
      const descriptions = {
        'modo-claro-oscuro': 'Sistema de toggle entre modo claro y oscuro con persistencia de preferencias del usuario.',
        'responsive-control': 'Diseño adaptable con control avanzado de breakpoints y layouts responsivos.',
        'animaciones-scroll': 'Animaciones suaves activadas al hacer scroll con optimización de rendimiento.',
        'web-ficticia': 'Sitio web completo con diseño moderno y todas las funcionalidades esenciales.',
        'web-mejorada': 'Rediseño y optimización de sitio web existente con mejoras de UX/UI.',
        'clon-netflix': 'Réplica funcional de la interfaz de Netflix con carrusel interactivo.'
      };
      return descriptions[this.currentProjectId] || 'Demostración interactiva de funcionalidades web.';
    },

    getDemoTechnologies () {
      const technologies = {
        'modo-claro-oscuro': ['Vue 3', 'CSS Variables', 'LocalStorage', 'Composition API'],
        'responsive-control': ['Vue 3', 'CSS Grid', 'Flexbox', 'Media Queries'],
        'animaciones-scroll': ['Vue 3', 'CSS Animations', 'Intersection Observer'],
        'web-ficticia': ['Vue 3', 'Vue Router', 'CSS3', 'JavaScript ES6+'],
        'web-mejorada': ['Vue 3', 'Optimización', 'Performance', 'UX/UI Design'],
        'clon-netflix': ['Vue 3', 'Carousel', 'Hover Effects', 'Responsive Design']
      };
      return technologies[this.currentProjectId] || ['Vue 3', 'JavaScript', 'CSS3', 'HTML5'];
    },

    getDemoFeatures () {
      const features = {
        'modo-claro-oscuro': [
          'Toggle instantáneo claro/oscuro',
          'Persistencia en localStorage',
          'Transiciones suaves CSS',
          'Variables CSS dinámicas'
        ],
        'responsive-control': [
          'Breakpoints adaptativos',
          'Layouts flexibles',
          'Optimización móvil',
          'Navegación responsive'
        ],
        'animaciones-scroll': [
          'Animaciones on-scroll',
          'Optimización de rendimiento',
          'Efectos parallax',
          'Transiciones CSS'
        ],
        'web-ficticia': [
          'Diseño moderno y limpio',
          'Navegación intuitiva',
          'Optimización SEO',
          'Código mantenible'
        ],
        'web-mejorada': [
          'Mejoras de rendimiento',
          'Optimización UX/UI',
          'Código refactorizado',
          'Mejora accesibilidad'
        ],
        'clon-netflix': [
          'Carrusel interactivo',
          'Efectos hover avanzados',
          'Diseño responsive',
          'Interfaz fluida'
        ]
      };
      return features[this.currentProjectId] || [
        'Interfaz moderna',
        'Código optimizado',
        'Experiencia de usuario mejorada'
      ];
    }
  }
}
</script>

<style scoped>
/* ESTILOS EXISTENTES SE MANTIENEN... */

/* OVERLAY PARA MÓVIL */
.mobile-warning-overlay .overlay-content {
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 100, 100, 0.3);
}

.mobile-warning-content h2 {
  color: #ff6b6b;
  font-size: 1.8rem;
}

.warning-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.warning-note {
  font-size: 0.9rem;
  opacity: 0.8;
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  border-left: 4px solid #ff6b6b;
}

.mobile-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-direction: column;
}

.mobile-btn {
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.primary-btn {
  background: var(--color-primary);
  color: white;
}

.primary-btn:hover {
  background: #0a58ca;
  transform: translateY(-2px);
}

.secondary-btn {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.secondary-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.config-note {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.config-note p {
  margin: 0;
  opacity: 0.7;
}

/* BOTÓN DE CONFIGURACIÓN EN DROPDOWN */
.config-btn {
  color: #ffd93d !important;
  font-size: 0.85rem;
}

/* RESPONSIVE PARA OVERLAY MÓVIL */
@media (max-width: 768px) {
  .mobile-actions {
    flex-direction: column;
  }

  .mobile-btn {
    width: 100%;
  }
}

/* EL RESTO DE LOS ESTILOS EXISTENTES SE MANTIENEN IGUAL... */

.demo-manager {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1040;
}

/* OVERLAY EXPLICATIVO */
.demo-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  pointer-events: all;
}

.overlay-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
}

.overlay-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 500px;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.spiral-arrow {
  font-size: 3rem;
  margin-bottom: 1rem;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(180deg); }
}

.logo-container {
  margin: 1rem 0;
  filter: none !important;
}

.overlay-content h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--color-primary);
}

.overlay-content p {
  margin-bottom: 1.5rem;
  line-height: 1.5;
  opacity: 0.9;
}

/* CHECKBOX "NO MOSTRAR DE NUEVO" */
.dont-show-container {
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: white;
  font-size: 0.9rem;
  gap: 0.5rem;
}

.checkbox-input {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.checkbox-input:checked + .checkmark {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.checkbox-input:checked + .checkmark::after {
  content: '✓';
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-label:hover .checkmark {
  border-color: rgba(255, 255, 255, 0.8);
}

.understand-btn {
  background: var(--color-primary);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 0.5rem;
}

.understand-btn:hover {
  background: #0a58ca;
  transform: translateY(-2px);
}

/* DIÁLOGO DE INFORMACIÓN */
.info-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1070;
  pointer-events: all;
}

.info-dialog {
  background: var(--color-surface);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: dialogSlideIn 0.3s ease;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-header h3 {
  color: var(--color-text-primary);
  margin: 0;
  font-size: 1.3rem;
}

.close-dialog-btn {
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.close-dialog-btn:hover {
  color: var(--color-text-primary);
  background: rgba(255, 255, 255, 0.1);
}

.dialog-content {
  padding: 1.5rem;
}

.demo-info h4 {
  color: var(--color-primary);
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.demo-description {
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.tech-stack {
  margin-bottom: 1.5rem;
}

.tech-stack h5 {
  color: var(--color-text-primary);
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tech-tag {
  background: rgba(92, 141, 246, 0.15);
  color: var(--color-primary);
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(92, 141, 246, 0.3);
}

.demo-features h5 {
  color: var(--color-text-primary);
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.demo-features ul {
  color: var(--color-text-secondary);
  padding-left: 1.2rem;
  line-height: 1.5;
}

.demo-features li {
  margin-bottom: 0.5rem;
}

.dialog-footer {
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: right;
}

.dialog-close-btn {
  background: var(--color-primary);
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dialog-close-btn:hover {
  background: #0a58ca;
  transform: translateY(-1px);
}

/* CONTROLES DEMO */
.demo-controls {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1060;
  pointer-events: all;
}

.controls-toggle {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.controls-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.controls-dropdown {
  position: absolute;
  bottom: 70px;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.5rem;
  min-width: 200px;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-header {
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  color: white;
  font-size: 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.25rem 0;
}

.dropdown-item {
  width: 100%;
  background: none;
  border: none;
  color: white;
  padding: 0.75rem 1rem;
  text-align: left;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dropdown-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.info-btn {
  color: #4dabf7;
}

.exit-btn {
  color: #ff6b6b;
}
</style>
